import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  canWriteOff,
  formatMoney,
  fromCents,
  toCents,
  WRITE_OFF_LIMIT,
  type WriteOffsRequest,
} from '@k-management/shared';
import { useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { treasuryKeys } from './treasury-ui.js';

interface Closable {
  id: string;
  number: string;
  date: string;
  counterpartyName: string;
  residual: string;
}

/** The invoices of a list whose residual is a difference of rounding (1.81). */
export function withSmallResidual<T extends { residual: string }>(items: readonly T[]): T[] {
  return items.filter((item) => canWriteOff(item.residual));
}

export const SMALL_RESIDUAL_LABEL = `Residuo fino a ${formatMoney(WRITE_OFF_LIMIT)}`;

/**
 * «Chiudi tutte con abbuono» (1.81): the invoices shown with a residual up to
 * the limit, closed together after a confirmation that lists them.
 */
export function WriteOffAllButton({ items }: { items: readonly Closable[] }) {
  const queryClient = useQueryClient();
  const canWrite = useCan('tesoreria.write');
  const [asking, setAsking] = useState(false);
  const [closed, setClosed] = useState<number | null>(null);
  const closable = withSmallResidual(items);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<{ closed: number }>('/treasury/invoices/write-offs', {
        method: 'POST',
        body: { ids: closable.map((item) => item.id) } satisfies WriteOffsRequest,
      }),
    onSuccess: async (result) => {
      setAsking(false);
      setClosed(result.closed);
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
    },
  });
  if (!canWrite) return null;
  const total = fromCents(closable.reduce((sum, item) => sum + toCents(item.residual), 0));
  return (
    <>
      {closable.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setClosed(null);
            setAsking(true);
          }}
        >
          Chiudi tutte con abbuono ({closable.length})
        </button>
      )}
      {closed !== null && <span className="muted small">Chiuse con abbuono: {closed}.</span>}
      {asking && (
        <ConfirmDialog
          title={`Abbuono su ${closable.length === 1 ? '1 fattura' : `${closable.length} fatture`}`}
          confirmLabel="Chiudi con abbuono"
          busy={save.isPending}
          error={save.error?.message}
          onConfirm={() => save.mutate()}
          onCancel={() => setAsking(false)}
        >
          <p>
            Chiude senza movimento di banca il residuo di ognuna, in tutto {formatMoney(total)}. Gli
            abbuoni restano tra incassi e pagamenti e si possono togliere.
          </p>
          <ul className="small">
            {closable.slice(0, 20).map((item) => (
              <li key={item.id}>
                {item.number} del {formatDate(item.date)} · {item.counterpartyName} ·{' '}
                {formatMoney(item.residual)}
              </li>
            ))}
            {closable.length > 20 && <li>… e altre {closable.length - 20}</li>}
          </ul>
        </ConfirmDialog>
      )}
    </>
  );
}
